import { Popover, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import { APP_BODY_FONT_FAMILY } from '../chartTypography';
import {
  ANALYTICS_DIVIDER,
  ANALYTICS_POPOVER_BORDER,
  ANALYTICS_POPOVER_SHADOW,
  ANALYTICS_SURFACE,
} from '../analyticsColors';

const useStyles = makeStyles(() => ({
  infoTitle: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1.25,
    fontFamily: APP_BODY_FONT_FAMILY,
    marginBottom: '8px',
  },
  popoverContent: {
    width: 'min(400px, calc(100vw - 32px))',
    maxWidth: '100%',
    padding: '14px 16px',
    boxSizing: 'border-box',
    textAlign: 'left',
  },
  popoverList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    paddingLeft: '10px',
  },
  popoverIncluded: {
    fontSize: '12px',
    lineHeight: 1.35,
    color: '#333',
    fontFamily: APP_BODY_FONT_FAMILY,
    marginBottom: '2px',
    textAlign: 'left',
  },
  popoverIncludedLabel: {
    fontWeight: 600,
  },
  popoverDivider: {
    border: 0,
    borderTop: `1px solid ${ANALYTICS_DIVIDER}`,
    margin: '10px 0',
  },
  popoverRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
  },
  popoverDot: {
    width: '3px',
    height: '3px',
    minWidth: '3px',
    borderRadius: '50%',
    backgroundColor: '#000000',
    marginTop: '7px',
  },
  popoverBody: {
    fontSize: '12px',
    lineHeight: 1.35,
    color: '#333',
    fontFamily: APP_BODY_FONT_FAMILY,
    textAlign: 'left',
  },
  popoverPaper: {
    background: ANALYTICS_SURFACE,
    border: `1px solid ${ANALYTICS_POPOVER_BORDER}`,
    borderRadius: '12px',
    boxShadow: ANALYTICS_POPOVER_SHADOW,
    maxHeight: 'min(70vh, 560px)',
    overflowY: 'auto',
    overflowX: 'hidden',
  },
}));

function InfoPopover({ open, anchorEl, onClose, heading, scope = '', items = [] }) {
  const classes = useStyles();
  const isValidAnchor = Boolean(anchorEl && document.body.contains(anchorEl));
  const openState = Boolean(open && isValidAnchor);
  const itemKeyCounts = new Map();

  return (
    <Popover
      open={openState}
      anchorEl={anchorEl}
      onClose={(_, reason) => onClose(reason || 'popover-close')}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      marginThreshold={8}
      disableScrollLock
      PaperProps={{ className: classes.popoverPaper }}
      sx={{ zIndex: 1600 }}
    >
      <div className={classes.popoverContent}>
        <Typography className={classes.infoTitle}>{heading}</Typography>
        {scope && (
          <Typography className={classes.popoverIncluded}>
            <span className={classes.popoverIncludedLabel}>Included:</span> {scope}
          </Typography>
        )}
        {scope && items.length > 0 && <hr className={classes.popoverDivider} />}
        {items.length > 0 && (
          <div className={classes.popoverList}>
            {items.map((item) => {
              const occurrence = (itemKeyCounts.get(item) || 0) + 1;
              itemKeyCounts.set(item, occurrence);
              const itemKey = `info-popover-item-${item}-${occurrence}`;

              return (
                <div className={classes.popoverRow} key={itemKey}>
                  <span className={classes.popoverDot} />
                  <Typography className={classes.popoverBody}>{item}</Typography>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Popover>
  );
}

InfoPopover.propTypes = {
  open: PropTypes.bool.isRequired,
  anchorEl: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  onClose: PropTypes.func.isRequired,
  heading: PropTypes.string.isRequired,
  scope: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.string),
};

export default InfoPopover;
